import { useState, useEffect } from 'react'
import { Search, Eye, Download, Filter, X, Sparkles, FileSpreadsheet, FileText, Calendar, Users, Building2, MapPin, User as UserIcon, TrendingUp, ClipboardList, MessageSquare, Inbox, Info } from 'lucide-react'
import { reportAPI, getApi, districtsAPI, centersAPI, employeesAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { parsePaginatedResponse } from '../../lib/pagination'
import Pagination from '../../components/Pagination'

export default function ReportsAdminPage() {
  const { error: toastError } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [reports, setReports] = useState<any[]>([])
  const [districts, setDistricts] = useState<any[]>([])
  const [centers, setCenters] = useState<any[]>([])
  const [employees, setEmployees] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)
  const [selectedReport, setSelectedReport] = useState<any>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const today = new Date().toISOString().slice(0, 10)
  const firstOfMonth = today.slice(0, 8) + '01'
  const [filters, setFilters] = useState({
    from: firstOfMonth,
    to: today,
    district: '',
    training_centre: '',
    employee: '',
  })

  const buildParams = (pageArg = page) => {
    const params: any = { page: pageArg }
    if (filters.from) params.from = filters.from
    if (filters.to) params.to = filters.to
    if (filters.district) params.district = filters.district
    if (filters.training_centre) params.training_centre = filters.training_centre
    if (filters.employee) params.employee = filters.employee
    if (searchTerm) params.search = searchTerm
    return params
  }

  const loadReports = async (pageArg = page) => {
    setIsLoading(true)
    try {
      const res = await reportAPI.getAllReports(buildParams(pageArg))
      const parsed = parsePaginatedResponse(res.data)
      setReports(parsed.results)
      setCount(parsed.count)
      setHasNext(parsed.hasNext)
      setHasPrev(parsed.hasPrev)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load reports.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    districtsAPI.list().then((res) => setDistricts(res.data.results || res.data)).catch(() => {})
    centersAPI.list().then((res) => setCenters(res.data.results || res.data)).catch(() => {})
    employeesAPI.list(1).then((res) => setEmployees(res.data.results || res.data)).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadReports(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.from, filters.to, filters.district, filters.training_centre, filters.employee])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1)
      loadReports(1)
    }, 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const filtered = reports

  const centersForDistrict = filters.district
    ? centers.filter((c) => String(c.district) === filters.district)
    : centers

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    setIsExporting(true)
    try {
      const res = await getApi().get('/daily-reports/export/', {
        params: { file_format: format, ...buildParams() },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `IRCS_daily_reports.${format}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to export reports.'))
    } finally {
      setIsExporting(false)
    }
  }

  const resetFilters = () => {
    setFilters({ from: firstOfMonth, to: today, district: '', training_centre: '', employee: '' })
  }

  const hasActiveFilters = Boolean(
    filters.district || filters.training_centre || filters.employee ||
    filters.from !== firstOfMonth || filters.to !== today
  )

  const inputClass = "w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-7">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Reports
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            End-of-Day Reports
          </h1>
          <p className="text-sm text-neutral-500">View employee daily activity reports</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`group flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5 ${
              showFilters || hasActiveFilters
                ? 'border border-[#E4670F]/40 bg-[#E4670F]/10 text-[#C24A0A] hover:bg-[#E4670F]/15'
                : 'border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            <Filter className="w-4 h-4 transition-transform group-hover:scale-110" />
            Filters
            {hasActiveFilters && (
              <span className="w-1.5 h-1.5 rounded-full bg-[#E4670F]" />
            )}
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={isExporting}
            className="group flex items-center gap-2 px-4 py-2.5 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            <FileSpreadsheet className="w-4 h-4 transition-transform group-hover:scale-110" />
            XLSX
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isExporting}
            className="group flex items-center gap-2 px-4 py-2.5 border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            <FileText className="w-4 h-4 transition-transform group-hover:scale-110" />
            PDF
          </button>
        </div>
      </div>

      {/* ── Filters panel ── */}
      {showFilters && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center">
                <Filter className="w-3.5 h-3.5 text-white" />
              </div>
              <h2 className="text-sm font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                Filter Reports
              </h2>
            </div>
            <button
              onClick={resetFilters}
              className="text-xs font-medium text-[#C24A0A] hover:text-[#7A1F1F] transition-colors"
            >
              Reset to this month
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3.5">
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">From</label>
              <div className="relative group">
                <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <input
                  type="date" value={filters.from}
                  onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                  className="w-full pl-10 pr-3 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">To</label>
              <div className="relative group">
                <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <input
                  type="date" value={filters.to}
                  onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                  className="w-full pl-10 pr-3 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">District</label>
              <div className="relative group">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <select
                  value={filters.district}
                  onChange={(e) => setFilters({ ...filters, district: e.target.value, training_centre: '' })}
                  className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
                >
                  <option value="">All Districts</option>
                  {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Training Centre</label>
              <div className="relative group">
                <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <select
                  value={filters.training_centre}
                  onChange={(e) => setFilters({ ...filters, training_centre: e.target.value })}
                  className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
                >
                  <option value="">All Centres</option>
                  {centersForDistrict.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Employee</label>
              <div className="relative group">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <select
                  value={filters.employee}
                  onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                  className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
                >
                  <option value="">All Employees</option>
                  {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Search + results strip ── */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 group">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
          <input
            type="text"
            placeholder="Search by employee ID, name or date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
          />
        </div>
        <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl px-4 py-2.5 shadow-sm">
          <ClipboardList className="w-4 h-4 text-[#C24A0A]" />
          <span className="text-sm font-semibold text-neutral-800 tabular-nums">{count}</span>
          <span className="text-xs text-neutral-500">{count === 1 ? 'report' : 'reports'}</span>
        </div>
      </div>

      {/* ── Reports list ── */}
      {isLoading ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
          <p className="text-sm text-neutral-500">Loading reports...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
            <Inbox className="w-8 h-8 text-neutral-400" />
          </div>
          <p className="text-sm font-medium text-neutral-700">No reports found</p>
          <p className="text-xs text-neutral-400">Try adjusting your filters or date range.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((report) => (
            <div
              key={report.id}
              className="group bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden hover:shadow-lg hover:shadow-neutral-200/50 hover:ring-neutral-300/70 transition-all duration-300"
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                  {/* Employee + date */}
                  <div className="flex items-start gap-3 lg:w-72 shrink-0">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-sm font-bold text-white ring-2 ring-white shadow-sm flex-shrink-0">
                      {(report.employee_name || report.employee_id || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-neutral-900 truncate">
                        {report.employee_name}
                        {report.employee_id && (
                          <span className="text-neutral-400 font-normal"> · {report.employee_id}</span>
                        )}
                      </p>
                      <p className="inline-flex items-center gap-1 text-xs text-neutral-500 mt-0.5">
                        <Calendar className="w-3 h-3" />
                        {report.date}
                      </p>
                    </div>
                  </div>

                  {/* Metrics grid */}
                  <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-blue-50/70 border border-blue-100 rounded-xl px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Users className="w-3 h-3 text-blue-600" />
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-blue-700">Registrations</p>
                      </div>
                      <p className="text-lg font-bold text-neutral-900 tabular-nums leading-none">
                        {report.total_registrations}
                      </p>
                    </div>

                    <div className="bg-violet-50/70 border border-violet-100 rounded-xl px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <TrendingUp className="w-3 h-3 text-violet-600" />
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-violet-700">Trainings</p>
                      </div>
                      <p className="text-lg font-bold text-neutral-900 tabular-nums leading-none">
                        {report.total_trainings_completed}
                      </p>
                    </div>

                    <div className="bg-emerald-50/70 border border-emerald-100 rounded-xl px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="w-3 h-3 rounded-full bg-emerald-500" />
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">Gross Fees</p>
                      </div>
                      <p className="text-lg font-bold text-neutral-900 tabular-nums leading-none">
                        ₹{report.gross_fees_received}
                      </p>
                    </div>

                    <div className="bg-amber-50/70 border border-amber-100 rounded-xl px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="w-3 h-3 rounded-full bg-amber-500" />
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">Net Fees</p>
                      </div>
                      <p className="text-lg font-bold text-neutral-900 tabular-nums leading-none">
                        ₹{report.net_fees_received}
                      </p>
                    </div>
                  </div>

                  {/* Action */}
                  <button
                    onClick={() => setSelectedReport(report)}
                    className="group/btn inline-flex items-center gap-2 px-4 py-2.5 border border-[#E4670F]/30 bg-[#E4670F]/[0.04] text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-xl font-medium text-sm transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap shrink-0"
                  >
                    <Eye className="w-4 h-4 transition-transform group-hover/btn:scale-110" />
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} onPageChange={setPage} count={count} hasNext={hasNext} hasPrev={hasPrev} />

      {/* ── Detail Modal ── */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30 flex-shrink-0">
                  <ClipboardList className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-neutral-900 truncate" style={{ fontFamily: 'Georgia, serif' }}>
                    Report Details
                  </h2>
                  <p className="text-xs text-neutral-500 truncate">{selectedReport.date}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedReport(null)}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto space-y-4">
              {/* Employee chip */}
              <div className="flex items-center gap-3 p-3.5 bg-gradient-to-br from-neutral-50 to-white border border-neutral-200 rounded-xl">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-sm font-bold text-white ring-2 ring-white shadow-sm flex-shrink-0">
                  {(selectedReport.employee_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 truncate">
                    {selectedReport.employee_name}
                  </p>
                  <p className="text-xs text-neutral-500 tabular-nums">{selectedReport.employee_id}</p>
                </div>
              </div>

              {/* Metrics grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50/70 border border-blue-100 rounded-xl p-3.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Users className="w-3.5 h-3.5 text-blue-600" />
                    <p className="text-[10px] uppercase tracking-wide font-semibold text-blue-700">Registrations</p>
                  </div>
                  <p className="text-2xl font-bold text-neutral-900 tabular-nums leading-none">
                    {selectedReport.total_registrations}
                  </p>
                </div>

                <div className="bg-violet-50/70 border border-violet-100 rounded-xl p-3.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp className="w-3.5 h-3.5 text-violet-600" />
                    <p className="text-[10px] uppercase tracking-wide font-semibold text-violet-700">Trainings</p>
                  </div>
                  <p className="text-2xl font-bold text-neutral-900 tabular-nums leading-none">
                    {selectedReport.total_trainings_completed}
                  </p>
                </div>

                <div className="bg-emerald-50/70 border border-emerald-100 rounded-xl p-3.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-3 h-3 rounded-full bg-emerald-500" />
                    <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">Gross Fees</p>
                  </div>
                  <p className="text-2xl font-bold text-neutral-900 tabular-nums leading-none">
                    ₹{selectedReport.gross_fees_received}
                  </p>
                </div>

                <div className="bg-amber-50/70 border border-amber-100 rounded-xl p-3.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-3 h-3 rounded-full bg-amber-500" />
                    <p className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">Net Fees</p>
                  </div>
                  <p className="text-2xl font-bold text-neutral-900 tabular-nums leading-none">
                    ₹{selectedReport.net_fees_received}
                  </p>
                </div>
              </div>

              {/* Remarks */}
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-neutral-500" />
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Remarks</p>
                </div>
                <p className="text-sm text-neutral-700 leading-relaxed">
                  {selectedReport.remarks || <span className="text-neutral-400 italic">No remarks provided.</span>}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-neutral-100 bg-neutral-50/60">
              <button
                onClick={() => setSelectedReport(null)}
                className="px-5 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// import { useState, useEffect } from 'react'
// import { Search, Eye, Download, Filter, X } from 'lucide-react'
// import { reportAPI, getApi, districtsAPI, centersAPI, employeesAPI } from '../../services/api'
// import { useToast, getErrorMessage } from '../../components/Toast'

// export default function ReportsAdminPage() {
//   const { error: toastError } = useToast()
//   const [searchTerm, setSearchTerm] = useState('')
//   const [reports, setReports] = useState<any[]>([])
//   const [districts, setDistricts] = useState<any[]>([])
//   const [centers, setCenters] = useState<any[]>([])
//   const [employees, setEmployees] = useState<any[]>([])
//   const [isLoading, setIsLoading] = useState(true)
//   const [selectedReport, setSelectedReport] = useState<any>(null)
//   const [showFilters, setShowFilters] = useState(false)
//   const [isExporting, setIsExporting] = useState(false)

//   const today = new Date().toISOString().slice(0, 10)
//   const firstOfMonth = today.slice(0, 8) + '01'
//   const [filters, setFilters] = useState({
//     from: firstOfMonth,
//     to: today,
//     district: '',
//     training_centre: '',
//     employee: '',
//   })

//   const buildParams = () => {
//     const params: any = {}
//     if (filters.from) params.from = filters.from
//     if (filters.to) params.to = filters.to
//     if (filters.district) params.district = filters.district
//     if (filters.training_centre) params.training_centre = filters.training_centre
//     if (filters.employee) params.employee = filters.employee
//     return params
//   }

//   const loadReports = async () => {
//     setIsLoading(true)
//     try {
//       const res = await reportAPI.getAllReports(buildParams())
//       setReports(res.data.results || res.data)
//     } catch (err: any) {
//       toastError(getErrorMessage(err, 'Failed to load reports.'))
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     districtsAPI.list().then((res) => setDistricts(res.data.results || res.data)).catch(() => {})
//     centersAPI.list().then((res) => setCenters(res.data.results || res.data)).catch(() => {})
//     employeesAPI.list(1).then((res) => setEmployees(res.data.results || res.data)).catch(() => {})
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [])

//   useEffect(() => {
//     loadReports()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [filters.from, filters.to, filters.district, filters.training_centre, filters.employee])

//   const filtered = reports.filter(
//     (r) =>
//       (r.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (r.employee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (r.date || '').includes(searchTerm)
//   )

//   const centersForDistrict = filters.district
//     ? centers.filter((c) => String(c.district) === filters.district)
//     : centers

//   const handleExport = async (format: 'xlsx' | 'pdf') => {
//     setIsExporting(true)
//     try {
//       const res = await getApi().get('/daily-reports/export/', {
//         params: { file_format: format, ...buildParams() },
//         responseType: 'blob',
//       })
//       const url = window.URL.createObjectURL(new Blob([res.data]))
//       const link = document.createElement('a')
//       link.href = url
//       link.setAttribute('download', `IRCS_daily_reports.${format}`)
//       document.body.appendChild(link)
//       link.click()
//       link.remove()
//     } catch (err: any) {
//       toastError(getErrorMessage(err, 'Failed to export reports.'))
//     } finally {
//       setIsExporting(false)
//     }
//   }

//   const resetFilters = () => {
//     setFilters({ from: firstOfMonth, to: today, district: '', training_centre: '', employee: '' })
//   }

//   return (
//     <div className="p-6 md:p-8">
//       <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
//         <div>
//           <h1 className="text-3xl font-bold text-secondary mb-2">End-of-Day Reports</h1>
//           <p className="text-neutral-600">View employee daily activity reports</p>
//         </div>
//         <div className="flex gap-2">
//           <button
//             onClick={() => setShowFilters((v) => !v)}
//             className="flex items-center gap-2 px-4 py-2 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-lg font-medium transition-colors"
//           >
//             <Filter className="w-4 h-4" />
//             Filters
//           </button>
//           <button
//             onClick={() => handleExport('xlsx')}
//             disabled={isExporting}
//             className="flex items-center gap-2 px-4 py-2 border border-primary text-primary hover:bg-red-50 rounded-lg font-medium transition-colors disabled:opacity-50"
//           >
//             <Download className="w-4 h-4" />
//             Export XLSX
//           </button>
//           <button
//             onClick={() => handleExport('pdf')}
//             disabled={isExporting}
//             className="flex items-center gap-2 px-4 py-2 border border-primary text-primary hover:bg-red-50 rounded-lg font-medium transition-colors disabled:opacity-50"
//           >
//             <Download className="w-4 h-4" />
//             Export PDF
//           </button>
//         </div>
//       </div>

//       {/* Filters panel */}
//       {showFilters && (
//         <div className="mt-6 bg-white rounded-lg shadow border border-neutral-200 p-6">
//           <div className="flex items-center justify-between mb-4">
//             <h2 className="font-semibold text-neutral-900">Filter Reports</h2>
//             <button onClick={resetFilters} className="text-xs text-primary font-medium hover:underline">
//               Reset to this month
//             </button>
//           </div>
//           <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
//             <div>
//               <label className="block text-xs font-medium text-neutral-600 mb-1">From</label>
//               <input
//                 type="date" value={filters.from}
//                 onChange={(e) => setFilters({ ...filters, from: e.target.value })}
//                 className="w-full px-3 py-2 border border-neutral-300 rounded-lg outline-none focus:ring-2 focus:ring-primary text-sm"
//               />
//             </div>
//             <div>
//               <label className="block text-xs font-medium text-neutral-600 mb-1">To</label>
//               <input
//                 type="date" value={filters.to}
//                 onChange={(e) => setFilters({ ...filters, to: e.target.value })}
//                 className="w-full px-3 py-2 border border-neutral-300 rounded-lg outline-none focus:ring-2 focus:ring-primary text-sm"
//               />
//             </div>
//             <div>
//               <label className="block text-xs font-medium text-neutral-600 mb-1">District</label>
//               <select
//                 value={filters.district}
//                 onChange={(e) => setFilters({ ...filters, district: e.target.value, training_centre: '' })}
//                 className="w-full px-3 py-2 border border-neutral-300 rounded-lg outline-none focus:ring-2 focus:ring-primary text-sm"
//               >
//                 <option value="">All Districts</option>
//                 {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
//               </select>
//             </div>
//             <div>
//               <label className="block text-xs font-medium text-neutral-600 mb-1">Training Centre</label>
//               <select
//                 value={filters.training_centre}
//                 onChange={(e) => setFilters({ ...filters, training_centre: e.target.value })}
//                 className="w-full px-3 py-2 border border-neutral-300 rounded-lg outline-none focus:ring-2 focus:ring-primary text-sm"
//               >
//                 <option value="">All Centres</option>
//                 {centersForDistrict.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
//               </select>
//             </div>
//             <div>
//               <label className="block text-xs font-medium text-neutral-600 mb-1">Employee</label>
//               <select
//                 value={filters.employee}
//                 onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
//                 className="w-full px-3 py-2 border border-neutral-300 rounded-lg outline-none focus:ring-2 focus:ring-primary text-sm"
//               >
//                 <option value="">All Employees</option>
//                 {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
//               </select>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Search */}
//       <div className="mt-6 mb-6">
//         <div className="relative">
//           <Search className="absolute left-3 top-3 w-5 h-5 text-neutral-400" />
//           <input
//             type="text"
//             placeholder="Search by employee ID, name or date..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
//           />
//         </div>
//       </div>

//       {/* Reports Grid */}
//       {isLoading ? (
//         <p className="text-neutral-600">Loading reports...</p>
//       ) : filtered.length === 0 ? (
//         <p className="text-neutral-600">No reports found for the selected filters.</p>
//       ) : (
//         <div className="space-y-4">
//           {filtered.map((report) => (
//             <div
//               key={report.id}
//               className="bg-white rounded-lg shadow border border-neutral-200 p-6 hover:shadow-lg transition-shadow"
//             >
//               <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
//                 <div className="flex-1">
//                   <div className="flex items-center gap-4 mb-3">
//                     <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
//                       {(report.employee_name || report.employee_id || '?').slice(0, 1)}
//                     </div>
//                     <div>
//                       <p className="font-semibold text-neutral-900">{report.employee_name} ({report.employee_id})</p>
//                       <p className="text-sm text-neutral-600">{report.date}</p>
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
//                     <div>
//                       <p className="text-xs text-neutral-600">Registrations</p>
//                       <p className="text-lg font-bold text-neutral-900">{report.total_registrations}</p>
//                     </div>
//                     <div>
//                       <p className="text-xs text-neutral-600">Trainings</p>
//                       <p className="text-lg font-bold text-neutral-900">{report.total_trainings_completed}</p>
//                     </div>
//                     <div>
//                       <p className="text-xs text-neutral-600">Gross Fees</p>
//                       <p className="text-lg font-bold text-neutral-900">₹{report.gross_fees_received}</p>
//                     </div>
//                     <div>
//                       <p className="text-xs text-neutral-600">Net Fees</p>
//                       <p className="text-lg font-bold text-neutral-900">₹{report.net_fees_received}</p>
//                     </div>
//                   </div>
//                 </div>

//                 <button
//                   onClick={() => setSelectedReport(report)}
//                   className="flex items-center gap-2 px-4 py-2 border border-primary text-primary hover:bg-red-50 rounded-lg font-medium transition-colors whitespace-nowrap"
//                 >
//                   <Eye className="w-4 h-4" />
//                   View Details
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>
//       )}

//       {/* Detail Modal */}
//       {selectedReport && (
//         <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
//             <div className="flex items-center justify-between mb-4">
//               <h2 className="text-2xl font-bold text-secondary">Report Details</h2>
//               <button onClick={() => setSelectedReport(null)} className="text-neutral-500 hover:text-neutral-700">
//                 <X className="w-5 h-5" />
//               </button>
//             </div>

//             <div className="space-y-4 mb-6">
//               <div className="flex justify-between items-center pb-2 border-b">
//                 <span className="text-neutral-600">Employee:</span>
//                 <span className="font-semibold">{selectedReport.employee_name} ({selectedReport.employee_id})</span>
//               </div>
//               <div className="flex justify-between items-center pb-2 border-b">
//                 <span className="text-neutral-600">Date:</span>
//                 <span className="font-semibold">{selectedReport.date}</span>
//               </div>
//               <div className="flex justify-between items-center pb-2 border-b">
//                 <span className="text-neutral-600">Registrations:</span>
//                 <span className="font-semibold">{selectedReport.total_registrations}</span>
//               </div>
//               <div className="flex justify-between items-center pb-2 border-b">
//                 <span className="text-neutral-600">Trainings:</span>
//                 <span className="font-semibold">{selectedReport.total_trainings_completed}</span>
//               </div>
//               <div className="flex justify-between items-center pb-2 border-b">
//                 <span className="text-neutral-600">Gross Fees:</span>
//                 <span className="font-semibold">₹{selectedReport.gross_fees_received}</span>
//               </div>
//               <div className="flex justify-between items-center pb-2 border-b">
//                 <span className="text-neutral-600">Net Fees:</span>
//                 <span className="font-semibold">₹{selectedReport.net_fees_received}</span>
//               </div>
//               <div>
//                 <span className="text-neutral-600">Remarks:</span>
//                 <p className="mt-2 text-neutral-700">{selectedReport.remarks || '—'}</p>
//               </div>
//             </div>

//             <button
//               onClick={() => setSelectedReport(null)}
//               className="w-full px-4 py-2 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-lg font-medium transition-colors"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }